import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faChevronDown, faChevronRight, faEdit, faBox, faMapPin, 
    faBuilding, faHashtag, faCalendar, faFileText, faTrash, faSpinner, faPlus
} from '@fortawesome/free-solid-svg-icons';
import StockItemVariantCard from './StockItemVariantCard';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LoadingSpinner from './ui/LoadingSpinner';
import './StockItemList.css';
import axiosInstance from '../utils/axiosConfig';
import { isBrandedCategory } from '../data/productTaxonomy';
import { getCategoryLabel, getSubtypeLabel } from '../data/productTaxonomy';

const StockItemList = ({
    items, loading, onRefresh, productType, category, onAddFirstItem, isSearchMode, searchResultsCount,
    totalCount = null, pageSize = 50, onLoadMore, loadingMore = false,
}) => {

    
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deletingItem, setDeletingItem] = useState(null);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(null);
    const [triggerAddVariant, setTriggerAddVariant] = useState(null);
    const [blockingRecords, setBlockingRecords] = useState(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionsDropdownOpen && !event.target.closest('.actions-dropdown-container')) {
                setActionsDropdownOpen(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [actionsDropdownOpen]);

    const handleViewContract = async (documentId) => {
        try {
            console.log(`Downloading contract: ${documentId}`);
            // Open contract document in new tab or modal
            const response = await axiosInstance.get(`/api/docuware/stock-contracts/download/${documentId}/`, {
                responseType: 'blob' // Important for file downloads
            });
            
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            console.log('Contract opened successfully');
        } catch (error) {
            console.error('Error downloading contract:', error);
            
            // Handle different error types
            if (error.response?.status === 401) {
                alert('Authentication required. Please log in again.');
            } else if (error.response?.status === 404) {
                alert('Contract not found.');
            } else {
                alert('Failed to download contract. Please try again.');
            }
        }
    };

    const toggleExpanded = (itemId) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const handleDeleteStockItem = (item) => {
        setItemToDelete(item);
        setDeleteModalOpen(true);
    };

    const confirmDeleteStockItem = async () => {
        if (!itemToDelete) return;

        const itemId = itemToDelete.id;
        setDeletingItem(itemId);
        
        try {
            await axiosInstance.delete(`/api/stock/stock-items/${itemId}/`);
            console.log('Stock item deleted successfully:', itemId);
            
            // Close modal and reset state
            setDeleteModalOpen(false);
            setItemToDelete(null);
            setBlockingRecords(null);
            
            // Refresh the list
            onRefresh();
        } catch (err) {
            console.error('Error deleting stock item:', err);
            
            // Check if this is a structured blocking error (HTTP 400 with blocking_records)
            if (err.response?.status === 400 && err.response?.data?.blocking_records) {
                // Extract structured error information
                const errorData = err.response.data;
                console.log('Product deletion blocked due to relationships:', errorData);
                
                // Store blocking records for modal display
                setBlockingRecords(errorData.blocking_records);
                
                // Throw error with detail message for modal to display
                throw new Error(errorData.detail || errorData.error || 'Cannot delete this product as it is linked to existing records.');
            }
            
            // Handle other error types - throw error to be handled by modal
            if (err.response?.status === 401) {
                throw new Error('You are not authorized to delete this stock item. Please log in again.');
            } else if (err.response?.status === 404) {
                throw new Error('Stock item not found. It may have already been deleted.');
            } else if (err.response?.status === 400) {
                // Generic 400 error without structured data
                const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Cannot delete stock item. It may have associated data that prevents deletion.';
                throw new Error(errorMsg);
            } else if (err.response?.status >= 500) {
                throw new Error('Server error. Please try again later.');
            } else {
                throw new Error('Failed to delete stock item. Please try again.');
            }
        } finally {
            setDeletingItem(null);
        }
    };

    const closeDeleteModal = () => {
        if (!deletingItem) {
            setDeleteModalOpen(false);
            setItemToDelete(null);
            setBlockingRecords(null);
        }
    };

    const toggleActionsDropdown = (itemId, e) => {
        e.stopPropagation();
        setActionsDropdownOpen(actionsDropdownOpen === itemId ? null : itemId);
    };

    const handleAddVariant = (item, e) => {
        e.stopPropagation();
        setActionsDropdownOpen(null);
        // Expand the item to show variants if not already expanded
        if (!expandedItems.has(item.id)) {
            toggleExpanded(item.id);
        }
        // Trigger the variant form opening
        setTriggerAddVariant(item.id);
        // Reset trigger after a brief moment
        setTimeout(() => setTriggerAddVariant(null), 100);
    };

    if (loading) {
        return <LoadingSpinner message="Loading product items..." size="medium" />;
    }

    if (!items.length) {
        return (
            <div className="empty-state">
                <FontAwesomeIcon icon={faBox} size="3x" className="empty-icon" />
                <h3>
                    {isSearchMode ? 'No Results Found' : 'No Product Items Found'}
                </h3>
                <p>
                    {isSearchMode
                        ? 'No product items match your search criteria. Try adjusting your search terms or filters.'
                        : productType
                        ? `No ${getSubtypeLabel(productType) || productType.replace('_', ' ')} items have been added yet.`
                        : category
                        ? `No ${getCategoryLabel(category) || category} items have been added yet.`
                        : 'No product items have been added yet.'
                    }
                </p>
                {!isSearchMode && (
                    <button className="btn btn-primary" onClick={onAddFirstItem}>
                        Add Your First Item
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="stock-item-list">
            {isSearchMode && searchResultsCount !== null && (
                <div className="search-results-header">
                    <h4>
                        {searchResultsCount} result{searchResultsCount !== 1 ? 's' : ''} found
                    </h4>
                </div>
            )}

            {/* Always say how much of the list is on screen: a list showing
              * fewer rows than exist looks exactly like a shorter list
              * (silent-list-truncation-class). The count is the server's. */}
            {!isSearchMode && totalCount !== null && (
                <div className="stock-list-count">
                    Showing {items.length} of {totalCount} product{totalCount !== 1 ? 's' : ''}
                </div>
            )}
            
            {items.map((item) => (
                <div key={item.id} className="stock-item-card">
                    {/* Base Item Header */}
                    <div className={`stock-item-header ${actionsDropdownOpen === item.id ? 'dropdown-expanded' : ''}`} onClick={() => toggleExpanded(item.id)}>
                        <div className="header-left">
                            <button className="expand-button">
                                <FontAwesomeIcon 
                                    icon={expandedItems.has(item.id) ? faChevronDown : faChevronRight} 
                                    size="sm"
                                />
                            </button>
                            <div className="item-info">
                                <div className="item-name-unified">
                                    {item.category_display && (
                                        <span className="category-badge">{item.category_display}</span>
                                    )}
                                    {/* Branded products (Lubrication) are named by brand +
                                      * product name. The bulk formula below is subtype +
                                      * origin + supplier, which for a lube range means
                                      * every one of ~900 rows reads the same word --
                                      * a catalogue nobody can browse.
                                      *
                                      * Read straight off ``branded_label``, never rebuilt
                                      * here: the rule includes not repeating a brand the
                                      * product name already carries, and a local copy of
                                      * it drifted within an hour of being written. */}
                                    {isBrandedCategory(item.category) ? (
                                        item.branded_label || item.dynamic_name
                                    ) : [
                                        item.subtype_label
                                            || item.product_type_display
                                            || item.product_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                        item.country_of_origin?.replace(/\b\w/g, l => l.toUpperCase()),
                                        item.source_location,
                                        // A partner's legal name renders exactly as CRM holds it, never re-cased here.
                                        item.supplier_crm_profile_data?.legal_entity_name
                                    ].filter(Boolean).join(' | ')}
                                    {item.has_contract && (
                                        <span className="contract-badge">Contract Available</span>
                                    )}
                                    {/* Supplier-list lifecycle, read off the server; an
                                      * uploaded list is the only thing that sets it. */}
                                    {item.retired_at && (
                                        <span className="contract-badge stock-retired-badge">
                                            Retired{item.replaced_by_label ? ` · now ${item.replaced_by_label}` : ''}
                                        </span>
                                    )}
                                    {item.replaces_labels?.length > 0 && (
                                        <span className="category-badge stock-replaces-badge">
                                            Replaces {item.replaces_labels.join(', ')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="header-right">
                            {item.has_contract && (
                                <button 
                                    className="contract-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewContract(item.source_contract_document_id);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileText} size="sm" />
                                    View Contract
                                </button>
                            )}
                            {/* A product with no items is invisible to every picker (invoices,
                                deliveries, recaps, fuel enquiries all search ITEMS, not products).
                                New products can no longer be created that way, but rows written
                                before that contract landed still exist — say so loudly instead of
                                showing a neutral "0 variants" badge that reads as normal.
                                (invoice-product-search-silent-drops, 2026-08-19) */}
                            {(item.variants_count || 0) === 0 ? (
                                <div
                                    className="variants-badge variants-badge-incomplete"
                                    title="This product has no items, so it cannot be picked on invoices, deliveries or recaps. Add an item to make it usable."
                                >
                                    ⚠ No items — not usable
                                </div>
                            ) : (
                                <div className="variants-badge">
                                    {item.variants_count} variant{item.variants_count !== 1 ? 's' : ''}
                                </div>
                            )}
                            <div className="actions-dropdown-container">
                                <button
                                    className="actions-dropdown-btn"
                                    title="Actions"
                                    onClick={(e) => toggleActionsDropdown(item.id, e)}
                                >
                                    Actions
                                </button>
                                
                                {actionsDropdownOpen === item.id && (
                                    <div className="actions-dropdown-menu">
                                        <button
                                            className="dropdown-item add-variant"
                                            onClick={(e) => handleAddVariant(item, e)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add Variant
                                        </button>
                                        <button
                                            className="dropdown-item delete-item"
                                            disabled={deletingItem === item.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActionsDropdownOpen(null);
                                                handleDeleteStockItem(item);
                                            }}
                                        >
                                            {deletingItem === item.id ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : (
                                                <FontAwesomeIcon icon={faTrash} />
                                            )}
                                            Delete Product Item
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Expanded Content - Variants */}
                    {expandedItems.has(item.id) && (
                        <div className="stock-item-variants">
                            <StockItemVariantCard
                                stockItemId={item.id}
                                parentSubtype={item.product_type}
                                onVariantUpdate={() => onRefresh()}
                                triggerAddVariant={triggerAddVariant === item.id}
                            />
                        </div>
                    )}
                </div>
            ))}

            {!isSearchMode && totalCount !== null && items.length < totalCount && onLoadMore && (
                <div className="stock-load-more-section">
                    <button
                        type="button"
                        className="stock-load-more-btn"
                        onClick={onLoadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore
                            ? 'Loading...'
                            : `Load ${Math.min(pageSize, totalCount - items.length)} more (${totalCount - items.length} remaining)`}
                    </button>
                </div>
            )}
            
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDeleteStockItem}
                title="Delete Product Item"
                message="Are you sure you want to delete this product item? This action will permanently delete the product item and ALL associated variants. This cannot be undone."
                itemName={itemToDelete?.dynamic_name || ""}
                loading={!!deletingItem}
                deleteButtonText="Delete Product Item"
                blockingRecords={blockingRecords}
            />
        </div>
    );
};

export default StockItemList; 